import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export type OnboardingStep = {
  id: string;
  title: string;
  body: React.ReactNode;
  selector?: string;
  route?: string;
  /**
   * When true, avoid scrolling the underlying layout automatically to keep the
   * highlighted element within the initial viewport. Useful for views where
   * scrolling causes the guided overlay to detach from its anchor.
   */
  preventAutoScroll?: boolean;
  /**
   * Locks the primary scroll containers while the step is active so the user
   * cannot accidentally move the page and lose the guided context.
   */
  lockScroll?: boolean;
  /**
   * Optional vertical position to restore when the step becomes active.
   */
  resetScrollTop?: number;
};

export type OnboardingTourContextValue = {
  steps: OnboardingStep[];
  isOpen: boolean;
  currentStepIndex: number;
  currentStep?: OnboardingStep;
  hasCompleted: boolean;
  start: () => void;
  stop: (options?: { completed?: boolean }) => void;
  reset: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  skipCurrentStep: () => void;
  completeTour: () => void;
};

const STORAGE_KEY = "onboardingTour=v1";

const OnboardingTourContext = createContext<OnboardingTourContextValue | undefined>(undefined);

const readHasCompletedFromStorage = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') {
      return false;
    }
    const record = parsed as Record<string, unknown>;
    return Boolean(record.completed);
  } catch (error) {
     
    console.warn("[OnboardingTour] Failed to read storage", error);
    return false;
  }
};

const persistCompletion = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ completed: true }));
  } catch (error) {
     
    console.warn("[OnboardingTour] Failed to persist completion", error);
  }
};

const clearCompletion = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
     
    console.warn("[OnboardingTour] Failed to clear completion", error);
  }
};

type OnboardingTourProviderProps = React.PropsWithChildren<{
  enabled?: boolean;
}>;

export const OnboardingTourProvider: React.FC<OnboardingTourProviderProps> = ({ children, enabled = true }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(() => readHasCompletedFromStorage());
  const pendingRouteRef = useRef<string | undefined>(undefined);

  const isAdmin = currentUser?.role === "admin";

  const steps = useMemo<OnboardingStep[]>(() => {
    const baseSteps: OnboardingStep[] = [
      {
        id: "welcome",
        title: "¡Bienvenido a Comuni Home!",
        body: (
          <span>
            Esta guía rápida te mostrará las novedades clave para gestionar tus comunidades: analíticas en vivo, automatizaciones inteligentes y accesos rápidos. Puedes volver a verla siempre que quieras desde el menú de usuario.
          </span>
        ),
        selector: '[data-onboarding="header-logo"]',
        route: "/app",
        preventAutoScroll: true,
      },
      {
        id: "dashboard",
        title: "Panel principal",
        body: (
          <span>
            Revisa indicadores en tiempo real, accesos directos a los módulos más usados y la nueva tira de novedades automatizadas que te alerta de tareas pendientes apenas inicias sesión.
          </span>
        ),
        selector: '[data-onboarding="nav-dashboard"]',
        route: "/app",
        preventAutoScroll: true,
      },
    ];

    if (isAdmin) {
      baseSteps.push({
        id: "scope",
        title: "Contexto multi-comunidad",
        body: (
          <span>
            Como administrador puedes combinar comunidades desde la barra de contexto para ver métricas agregadas, lanzar informes masivos o aplicar acciones en lote sobre todas ellas.
          </span>
        ),
        selector: '[data-onboarding="community-scope"]',
        route: "/app",
        preventAutoScroll: true,
      });
    }

    baseSteps.push(
      {
        id: "payments",
        title: "Pagos y cuotas",
        body: (
          <span>
            Gestiona cobros, aplica filtros avanzados y ordena con un clic. Desde aquí también puedes lanzar recordatorios, marcar pagos en lote o importar resultados generados por el asistente inteligente.
          </span>
        ),
        selector: '[data-onboarding="payments-filters"]',
        route: "/app/payments",
      },
      {
        id: "paymentsSearchAssistant",
        title: "Búsqueda asistida",
        body: (
          <span>
            El nuevo panel de búsqueda acepta resultados del asistente inteligente y consultas guardadas. Úsalo para ejecutar flujos automáticos como exportaciones, creación de derramas o envíos de recordatorios en segundos.
          </span>
        ),
        selector: '[data-onboarding="payments-assistant"]',
        route: "/app/payments",
      },
      {
        id: "reservations",
        title: "Reservas de espacios",
        body: (
          <span>
            Consulta el calendario interactivo de espacios comunes, bloquea franjas críticas y ofrece a tus vecinos un resumen instantáneo de disponibilidad.
          </span>
        ),
        selector: '[data-onboarding="reservations-calendar"]',
        route: "/app/reservations",
        preventAutoScroll: true,
        lockScroll: true,
        resetScrollTop: 0,
      },
      {
        id: "finish",
        title: "¡Tour completado!",
        body: (
          <span>
            Ya conoces las nuevas capacidades de Comuni Home. Si necesitas recordarlo más adelante, pulsa "Ver tour" desde el menú de usuario o abre la búsqueda asistida para descubrir más atajos.
          </span>
        ),
        selector: '[data-onboarding="tour-reopen"]',
        route: "/app",
      }
    );

    return baseSteps;
  }, [isAdmin]);

  const totalSteps = steps.length;

  const stop = useCallback((options?: { completed?: boolean }) => {
    setIsOpen(false);
    if (options?.completed) {
      setHasCompleted(true);
      persistCompletion();
    }
  }, []);

  const completeTour = useCallback(() => {
    stop({ completed: true });
  }, [stop]);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const next = prev + 1;
      if (next >= totalSteps) {
        completeTour();
        return prev;
      }
      return next;
    });
  }, [completeTour, totalSteps]);

  const skipCurrentStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const next = prev + 1;
      if (next >= totalSteps) {
        completeTour();
        return prev;
      }
      return next;
    });
  }, [completeTour, totalSteps]);

  const previousStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const skipTour = useCallback(() => {
    completeTour();
  }, [completeTour]);

  const start = useCallback(() => {
    if (!enabled) return;
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, [enabled]);

  const reset = useCallback(() => {
    clearCompletion();
    setHasCompleted(false);
    setCurrentStepIndex(0);
    setIsOpen(false);
  }, []);

  const currentStep = steps[currentStepIndex];

  useEffect(() => {
    if (!enabled) {
      if (isOpen) {
        setIsOpen(false);
      }
      pendingRouteRef.current = undefined;
      return;
    }
    if (!isOpen) {
      pendingRouteRef.current = undefined;
      return;
    }
    if (currentStep?.route && currentStep.route !== location.pathname) {
      pendingRouteRef.current = currentStep.route;
      navigate(currentStep.route);
    } else {
      pendingRouteRef.current = undefined;
    }
  }, [currentStep, enabled, isOpen, location.pathname, navigate]);

  useEffect(() => {
    if (!enabled || !isOpen) return;
    if (!pendingRouteRef.current) return;
    if (location.pathname === pendingRouteRef.current) {
      pendingRouteRef.current = undefined;
    }
  }, [enabled, location.pathname, isOpen]);

  useEffect(() => {
    if (!enabled) return;
    if (currentUser && !hasCompleted && !isOpen) {
      start();
    }
  }, [currentUser, enabled, hasCompleted, isOpen, start]);

  const value = useMemo<OnboardingTourContextValue>(
    () => ({
      steps,
      isOpen,
      currentStepIndex,
      currentStep,
      hasCompleted,
      start,
      stop,
      reset,
      nextStep,
      previousStep,
      skipTour,
      skipCurrentStep,
      completeTour,
    }),
    [steps, isOpen, currentStepIndex, currentStep, hasCompleted, start, stop, reset, nextStep, previousStep, skipTour, skipCurrentStep, completeTour]
  );

  return (
    <OnboardingTourContext.Provider value={value}>{children}</OnboardingTourContext.Provider>
  );
};

export const useOnboardingTour = () => {
  const context = useContext(OnboardingTourContext);
  if (!context) {
    throw new Error("useOnboardingTour must be used within an OnboardingTourProvider");
  }
  return context;
};
